import type { AsrQuality, CaptionLanguage, CaptionSettings } from '../captions/types';
import type { ScanPrecision } from '../detection/scan-plan';
import type { DetectionKind } from '../detection/types';
import { DEFAULT_TELEMETRY_SETTINGS, type TelemetrySettings } from '../telemetry/overlay';
import { DEFAULT_ZONE_SETTINGS, type ZoneSettings } from '../telemetry/zones';
import { DEFAULT_CAMERA_SETTINGS, parseCameraSettings, type CameraSettings } from '../camera/layer';
import { DEFAULT_ZOOM_SETTINGS, parseZoomSettings, type ZoomSettings } from '../screen/zoom';
import { DEFAULT_PRIVACY, parsePrivacy, type FormatSettings, type PrivacySettings, type AudioSettings, type EditRecipe, type QualityId } from './recipe';

/** How hiding looks travels with a recipe (the person's own sticker too); what was found or drawn stays with its video. */
type PresetPrivacyKey = 'enabled' | 'style' | 'emoji' | 'sticker' | 'image' | 'placement' | 'strength' | 'padding';

function privacyLook(privacy: PrivacySettings): Pick<PrivacySettings, PresetPrivacyKey> {
  const { enabled, style, emoji, sticker, image, placement, strength, padding } = privacy;
  return { enabled, style, emoji, sticker, image, placement, strength, padding };
}

/**
 * The reusable, video-independent part of an edit. Anything tied to one
 * specific video (trim points, framing focus, found faces, drawn areas,
 * caption text) is deliberately left out.
 */
export interface PresetSettings {
  readonly silence: EditRecipe['silence'];
  readonly fillers: EditRecipe['fillers'];
  readonly format: Omit<FormatSettings, 'focus'>;
  readonly privacy: Pick<PrivacySettings, PresetPrivacyKey> & {
    /** What to scan for automatically; empty means no automatic scan. */
    readonly scan: readonly DetectionKind[];
    readonly precision: ScanPrecision;
  };
  readonly audio: AudioSettings;
  readonly captions: Pick<CaptionSettings, 'enabled' | 'style' | 'position' | 'size'> & {
    /** Transcribe automatically when the preset is applied. */
    readonly auto: boolean;
    readonly language: CaptionLanguage;
    readonly quality: AsrQuality;
  };
  /** Units are a per-person preference, not part of a recipe. */
  readonly telemetry: Omit<TelemetrySettings, 'units'>;
  /** How private places are handled; the places themselves stay on the device, outside recipes. */
  readonly zones: ZoneSettings;
  /** Camera placement for screen recordings; harmless for videos without a camera layer. */
  readonly camera: CameraSettings;
  /** Whether and how much to zoom; which moments were turned off belongs to one video. */
  readonly zoom: Omit<ZoomSettings, 'skipped'>;
  readonly quality: QualityId;
}

/**
 * Recipes for what people make (recording, reels, youtube, motovlog), recipes
 * that do one job (faces, plates, fillers, captions) and a blank one that does
 * nothing, which is also what opens when nothing was chosen. Three more serve
 * the content pages' links for their own job: faces and plates together
 * (privacy), pauses and hesitations (silence), and a subtitle file that isn't
 * burned into the picture (subtitles).
 */
export type BuiltInPresetId = 'recording' | 'reels' | 'youtube' | 'motovlog' | 'privacy' | 'faces' | 'plates' | 'fillers' | 'captions' | 'blank' | 'silence' | 'subtitles';

export interface Preset {
  readonly id: string;
  /** User presets carry a name; built-ins are named through translations. */
  readonly name?: string;
  readonly builtIn?: BuiltInPresetId;
  readonly settings: PresetSettings;
}

export interface PresetContext {
  readonly scan: readonly DetectionKind[];
  readonly precision: ScanPrecision;
  readonly captionLanguage: CaptionLanguage;
  readonly captionQuality: AsrQuality;
  /** Whether the current video already has captions (then new videos should get them too). */
  readonly hasCaptions: boolean;
  /** Whether the current video was scanned (then new videos should be too). */
  readonly scanned: boolean;
}

export function presetFromRecipe(recipe: EditRecipe, context: PresetContext): PresetSettings {
  const { focus: _focus, ...format } = recipe.format;
  return {
    silence: recipe.silence,
    fillers: recipe.fillers,
    format,
    privacy: {
      ...privacyLook(recipe.privacy),
      scan: context.scanned ? context.scan : [],
      precision: context.precision,
    },
    audio: recipe.audio,
    captions: {
      enabled: recipe.captions.enabled,
      style: recipe.captions.style,
      position: recipe.captions.position,
      size: recipe.captions.size,
      auto: context.hasCaptions,
      language: context.captionLanguage,
      quality: context.captionQuality,
    },
    telemetry: telemetryPart(recipe.telemetry),
    zones: recipe.zones,
    camera: recipe.camera,
    zoom: { enabled: recipe.zoom.enabled, amount: recipe.zoom.amount },
    quality: recipe.quality,
  };
}

function telemetryPart({ units: _units, ...rest }: TelemetrySettings): PresetSettings['telemetry'] {
  return rest;
}

/** Applies preset settings while keeping everything that belongs to the current video. */
export function applyPreset(recipe: EditRecipe, settings: PresetSettings): EditRecipe {
  const { scan: _scan, precision: _precision, ...privacy } = settings.privacy;
  const { auto: _auto, language: _language, quality: _quality, ...captions } = settings.captions;
  return {
    ...recipe,
    silence: settings.silence,
    fillers: settings.fillers,
    format: { ...settings.format, focus: recipe.format.focus },
    privacy: { ...recipe.privacy, ...privacy },
    audio: settings.audio,
    captions: { ...recipe.captions, ...captions, enabled: captions.enabled && (recipe.captions.cues.length > 0 || settings.captions.auto) },
    telemetry: { ...settings.telemetry, units: recipe.telemetry.units },
    zones: settings.zones,
    camera: settings.camera,
    zoom: { ...settings.zoom, skipped: recipe.zoom.skipped },
    quality: settings.quality,
  };
}

const BASE: PresetSettings = {
  silence: { enabled: false, preset: 'balanced' },
  fillers: { enabled: false },
  format: { aspect: 'original', fit: 'crop', resolution: 'source', followSubject: false },
  privacy: { ...privacyLook(DEFAULT_PRIVACY), scan: [], precision: 'fast' },
  audio: { muted: false, normalize: true, denoise: false },
  captions: { enabled: false, style: 'pop', position: 'bottom', size: 0.5, auto: false, language: 'auto', quality: 'accurate' },
  telemetry: telemetryPart(DEFAULT_TELEMETRY_SETTINGS),
  zones: DEFAULT_ZONE_SETTINGS,
  camera: DEFAULT_CAMERA_SETTINGS,
  zoom: { enabled: DEFAULT_ZOOM_SETTINGS.enabled, amount: DEFAULT_ZOOM_SETTINGS.amount },
  quality: 'balanced',
};

/** Ready-made recipes for the most common jobs. */
export const BUILT_IN_PRESETS: readonly Preset[] = [
  {
    id: 'builtin-reels',
    builtIn: 'reels',
    settings: {
      ...BASE,
      silence: { enabled: true, preset: 'balanced' },
      fillers: { enabled: true },
      format: { aspect: '9:16', fit: 'crop', resolution: '1080', followSubject: true },
      privacy: { ...BASE.privacy, scan: ['face', 'plate'] },
      captions: { ...BASE.captions, enabled: true, auto: true },
    },
  },
  {
    id: 'builtin-youtube',
    builtIn: 'youtube',
    settings: {
      ...BASE,
      silence: { enabled: true, preset: 'gentle' },
      fillers: { enabled: true },
      format: { aspect: '16:9', fit: 'blurFill', resolution: '1080', followSubject: false },
      quality: 'high',
    },
  },
  {
    id: 'builtin-motovlog',
    builtIn: 'motovlog',
    settings: {
      ...BASE,
      silence: { enabled: true, preset: 'gentle' },
      privacy: { ...BASE.privacy, scan: ['face', 'plate'], precision: 'thorough' },
      audio: { muted: false, normalize: true, denoise: true },
      // Shown only when the video carries GPS data (GoPro); harmless otherwise.
      telemetry: { ...BASE.telemetry, enabled: true },
    },
  },
  {
    id: 'builtin-privacy',
    builtIn: 'privacy',
    settings: { ...BASE, privacy: { ...BASE.privacy, scan: ['face', 'plate'], precision: 'thorough' } },
  },
  {
    id: 'builtin-faces',
    builtIn: 'faces',
    settings: { ...BASE, privacy: { ...BASE.privacy, scan: ['face'], precision: 'thorough' } },
  },
  {
    id: 'builtin-plates',
    builtIn: 'plates',
    settings: { ...BASE, privacy: { ...BASE.privacy, scan: ['plate'], precision: 'thorough' } },
  },
  {
    // Hesitations only ("um", "ııı"): found in the sound itself, so nothing is downloaded.
    id: 'builtin-fillers',
    builtIn: 'fillers',
    settings: { ...BASE, fillers: { enabled: true } },
  },
  {
    id: 'builtin-captions',
    builtIn: 'captions',
    settings: { ...BASE, captions: { ...BASE.captions, enabled: true, auto: true } },
  },
  {
    // Nothing applied: the editor as it opens on its own, every tool one tap away.
    id: 'builtin-blank',
    builtIn: 'blank',
    settings: BASE,
  },
  {
    // Pauses and hesitations cut, the frame left as filmed (the silence page's link).
    id: 'builtin-silence',
    builtIn: 'silence',
    settings: { ...BASE, silence: { enabled: true, preset: 'balanced' }, fillers: { enabled: true } },
  },
  {
    // Captions written to save as an SRT file, not burned into the picture (the subtitle pages' links).
    id: 'builtin-subtitles',
    builtIn: 'subtitles',
    settings: { ...BASE, captions: { ...BASE.captions, enabled: false, auto: true } },
  },
  {
    // Screen recordings: pauses and dead air trimmed, voice cleaned up, sharp text.
    // Faces on screen (video calls) are hidden; the presenter's own camera is a separate layer and never scanned.
    id: 'builtin-recording',
    builtIn: 'recording',
    settings: {
      ...BASE,
      silence: { enabled: true, preset: 'gentle' },
      fillers: { enabled: true },
      privacy: { ...BASE.privacy, scan: ['face'] },
      audio: { muted: false, normalize: true, denoise: true },
      captions: { ...BASE.captions, auto: true },
      zoom: { enabled: true, amount: 1.8 },
      quality: 'high',
    },
  },
];

/**
 * Parses stored user presets defensively; corrupt entries are dropped, never
 * thrown. Presets saved before a setting existed get its defaults.
 */
export function parseStoredPresets(raw: string | null): Preset[] {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    return value.filter(
      (item): item is Preset =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as Preset).id === 'string' &&
        typeof (item as Preset).name === 'string' &&
        typeof (item as Preset).settings === 'object' &&
        (item as Preset).settings !== null &&
        typeof (item as Preset).settings.format === 'object',
    ).map((preset) => ({
      ...preset,
      settings: {
        ...preset.settings,
        fillers: { enabled: preset.settings.fillers?.enabled === true },
        privacy: { ...BASE.privacy, ...preset.settings.privacy, ...privacyLook(parsePrivacy(preset.settings.privacy)) },
        telemetry: { ...BASE.telemetry, ...preset.settings.telemetry },
        zones: { ...BASE.zones, ...preset.settings.zones },
        camera: parseCameraSettings(preset.settings.camera),
        zoom: (({ enabled, amount }) => ({ enabled, amount }))(parseZoomSettings(preset.settings.zoom)),
      },
    }));
  } catch {
    return [];
  }
}
